/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    DocAddonBridge,
    DialogAddonWindow,
    DocElementType,
    DocElement,
    WebAddonElement,
    ImageAddonElement,
    ImageSourceType,
    DocService,
    DocEditorService,
    DocAdvancedService,
    DocConfigService,
} from '@tencent/td-addon-sdk-docs';
import { IS_PRIVATE_WECOM } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { AddonIframeOptions } from './configure/addon-iframe.interface';
import { uploadImageBlob } from '@src/slide/infrastructure/module/dependent/image/image-toolkit/cgi-upload';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import i18n from '@i18next/i18n';
import { ADDON_ID_FORMULA } from './addon-constants';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { AddonMode } from '@src/slide/domain/ui-model/element-modeles/property/addon/addon.interface';
import { addUrlParam } from '@src/slide/infrastructure/module/independent/url/param-operation';
import { IAddonManager } from './addon-manager.interface';
import ImageUrl from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-url';
import ImageQuickLookManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';

export interface AddonRightSidebarOptions {
    title: string;
    html: string;
    aid: string;
    width?: number;
    hiddenClose?: boolean; // 是否隐藏关闭按钮 Default:NO
    hiddenHeader?: boolean; // 是否隐藏Header Default:NO
    completeOpenDialog?: () => void;
}
export interface AddonDialogOptions extends AddonRightSidebarOptions {
    width: number;
    height: number;
}

type SelectedModelType = Promise<DocElement | WebAddonElement | ImageAddonElement>;

/**
 * 提供给插件的接口能力
 */
export default class AddonAbility {
    // 存储插入内容类型与相对应插入方法的集合
    private insertDocElementFnMap: any;
    private updateDocElementFnMap: any;

    public constructor(private addonManagerDelegate: IAddonManager, private selectionService: ISelectionService) {
        this.insertDocElementFnMap = {
            [DocElementType.IMAGE]: this.insertImageAddonElement,
            [DocElementType.WEB]: this.insertWebAddonElement,
        };
        this.updateDocElementFnMap = {
            [DocElementType.IMAGE]: this.updateImageAddonElement,
            [DocElementType.WEB]: this.updateWebAddonElement,
        };
    }

    // 汇总插件所需所有接口
    public get docService(): DocService {
        return {
            config: this.getDocConfigService(),
            ui: {},
            editor: this.getDocEditorService(),
            advanced: this.getDocAdvancedService(),
        };
    }

    // 获取光标信息，目前暂未实现
    public getCursor = (): any => undefined;

    // 获取选中元素信息
    public getSelectedDocElements = async (): Promise<DocElement[]> => {
        const selectedModel: any = this.selectionService.getSelectedModel();

        // 选中了多个元素
        if (selectedModel?.type === 'Multiply') {
            const { selectedModels = [] } = selectedModel;
            const elementList: DocElement[] = [];
            selectedModels.forEach(async (item) => {
                const element = await this.parseSelectedModel(item);
                elementList.push(element);
            });
            return elementList;
        }

        // 选中单个元素
        if (selectedModel) {
            const element = await this.parseSelectedModel(selectedModel);
            return [element];
        }

        // 没有选中元素
        return [];
    };

    // 插入插件内容
    public insertDocElement = (docElement: DocElement): void => {
        const docElementType: number = DocAddonBridge.getDocElementType(docElement);
        const insertDocElement: any = this.insertDocElementFnMap[docElementType];
        insertDocElement?.(docElement);
    };

    // 更新插件内容
    public updateDocElement = (docElement: DocElement): void => {
        const docElementType: number = DocAddonBridge.getDocElementType(docElement);
        const updateDocElement: any = this.updateDocElementFnMap[docElementType];
        updateDocElement?.(docElement);
    };

    // 上传图片接口
    public uploadImage = (imageData?: Blob): Promise<string> => {
        if (IS_PRIVATE_WECOM && imageData) {
            return new Promise((resolve, reject) => {
                uploadImageBlob({ blob: imageData })
                    .then((res) => {
                        const { url, width, height } = res;
                        const imageURL = addUrlParam(url, { w: width, h: height });
                        resolve(imageURL);
                    })
                    .catch((errorTip) => {
                        reject(errorTip);
                    });
            });
        }

        return Promise.resolve('');
    };

    public getAddonIframeOptions = (webAddonElement: WebAddonElement): AddonIframeOptions => ({
        title: webAddonElement.displayText || '',
        embedUrl: webAddonElement.embedURL,
        originUrl: webAddonElement.originURL,
        width: webAddonElement.width,
        height: webAddonElement.height,
        aid: webAddonElement.addonId,
        extraData: webAddonElement.extraData,
        mode: AddonMode.IFRAME,
        docElement: webAddonElement,
    });

    // 插入幻灯片文件接口，目前用于插入iSlide插件内容
    public importSlide = async (file: File): Promise<void> => {
        const importFileManager = await (this.addonManagerDelegate.importFileManagerDelegate?.promise());
        return importFileManager?.parseAndInsertISlideFile(file);
    };

    private getDocConfigService = (): DocConfigService => ({
        getLanguage: () => i18n.language,
    });

    private getDocEditorService = (): DocEditorService => ({
        getCursor: this.getCursor,
        getSelectedDocElements: this.getSelectedDocElements,
        insertDocElement: this.insertDocElement,
        updateDocElement: this.updateDocElement,
    });

    private getDocAdvancedService = (): DocAdvancedService => ({
        uploadImage: this.uploadImage,
        importSlide: this.importSlide,
        callOCR: this.addonManagerDelegate.uploadPictureFormulaOcr,
    });

    private getAddonDialogOptions = (dialogAddonWindow: DialogAddonWindow): AddonDialogOptions => ({
        aid: dialogAddonWindow.id,
        html: dialogAddonWindow.src,
        title: dialogAddonWindow.title || '',
        width: dialogAddonWindow.width || 0,
        height: dialogAddonWindow.height || 0,
        hiddenClose: false,
        hiddenHeader: false,
    });

    private onLoadDialog = (): void => undefined;

    // 插入图片内容
    private insertImageAddonElement = (imageAddonElement: ImageAddonElement): void => {
        this.addonManagerDelegate.insertAddonPicture(imageAddonElement);
    };

    // 插入网页内容，用于“插入第三方内容”
    private insertWebAddonElement = (webAddonElement: WebAddonElement): void => {
        const addonIframeOptions: AddonIframeOptions = this.getAddonIframeOptions(webAddonElement);
        this.addonManagerDelegate.insertIframeContainer(addonIframeOptions);

        tdw({
            opername: TDWOperationType.DOC_ADDON_PLATFORM,
            module: TDWModuleType.ADDON_IFRAME,
            action: TDWActionType.ADDON_INSERT,
            ver6: addonIframeOptions.aid,
        });
    };

    private updateImageAddonElement = (imageAddonElement: ImageAddonElement): void => {
        this.addonManagerDelegate.updateAddonPicture(imageAddonElement);
    };

    private updateWebAddonElement = (webAddonElement: WebAddonElement): void => {
        this.addonManagerDelegate.refreshIframe(
            webAddonElement.originURL,
            webAddonElement.embedURL,
            webAddonElement.displayText,
        );
    };

    // 获取随机文档元素id字符串，一共6位，由数字和字母租成
    private getRandomAnchorId = (): string => Math.random().toString(36)
        .substr(2, 6);

    private getAddonElementForNotAddon = async (selectedModel): Promise<ImageAddonElement | DocElement> => {
        if (selectedModel.type === UIModelType.IMAGE) {
            let src = selectedModel.props?.src || '';

            if (ImageUrl.isBlobUrl(src)) {
                const ImageResource = ImageQuickLookManager.getInstance().getResourceByUrl(src);
                const cdnUrl = ImageResource?.getCdnUrl();
                if (cdnUrl) {
                    src = cdnUrl;
                } else {
                    const promise = new Promise((reslove, reject) => {
                        ImageResource?.onDidUpload((imageResource) => {
                            reslove(imageResource.getCdnUrl());
                        });
                        ImageResource?.onUploadError(() => {
                            reject('');
                        });
                    });
                    src = await promise;
                }
            }
            return {
                anchorId: selectedModel.key,
                addonId: '',
                src,
                sourceType: ImageSourceType.PIC,
            };
        }
        return {};
    };

    private parseSelectedModel = async (selectedModel: any): SelectedModelType => {
        const addon = selectedModel?.props?.addon;

        // 导入的公式
        if (selectedModel?.isTextMath) {
            return {
                anchorId: selectedModel.key || this.getRandomAnchorId(),
                addonId: ADDON_ID_FORMULA,
                extraData: '',
                src: selectedModel.props?.style?.fillBackground?.image?.src || '',
                sourceType: ImageSourceType.LATEX,
            };
        }

        // 如果没有插件数据
        if (!addon) {
            return await this.getAddonElementForNotAddon(selectedModel);
        }

        // 如果存在插件数据，且插件数据中包含docElement字段，则判断为新sdk插入的插件内容，直接返回docElement即可
        // 目前由于docElement中的displayText字段为undefined，所以暂时屏蔽此逻辑，待后续补充
        if (addon?.docElement) {
            return {
                ...addon.docElement,
                displayText: addon.title,
            };
        }

        // 如果存在插件数据，且包含iframeId字段，则判断为旧sdk插入的iframe插件内容
        if (addon?.iframeId) {
            return {
                anchorId: this.getRandomAnchorId(),
                addonId: addon.iframeId,
                extraData: addon.extraData,
                embedURL: addon.embedUrl,
                originURL: addon.originUrl,
                displayText: addon.title,
            };
        }

        // 如果存在插件数据，且type字段值未latex，则判断为latex公式图片的插件内容
        if (addon?.type === 'latex') {
            return {
                anchorId: this.getRandomAnchorId(),
                addonId: addon.aid,
                extraData: addon.extraData,
                src: selectedModel.props?.src || '',
                sourceType: ImageSourceType.LATEX,
            };
        }

        return {};
    };
}
